import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, App, Badge, Button, Checkbox, DatePicker, Descriptions, Drawer, Empty, Form, Grid, Input, InputNumber, Modal, Popover,
  Segmented, Select, Space, Spin, Tag, Tooltip, Typography, Upload, theme,
} from 'antd';
import {
  AppstoreOutlined, CameraOutlined, CheckCircleFilled, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, DeleteOutlined,
  EditOutlined, EnvironmentOutlined, ExclamationCircleFilled, FormOutlined, HourglassOutlined, LoadingOutlined, PlusOutlined,
  QuestionCircleOutlined, RightCircleFilled,
  RightOutlined, RollbackOutlined, SafetyCertificateOutlined, ScanOutlined, SendOutlined, WarningFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { inputNumberMoneyProps } from '../../utils/currency';
import {
  DURUM_RENK, FisDugmesi, FisKucuk, GecmisListesi, ISARET_TANIM, IsaretBandi, IsaretEtiketleri, KONUM_TANIM, KonumEtiketi, NEDEN_ONERILERI, OGUN_ADI,
  ciddiMi, SeyahatAkisi, TIP_ADI, fisiKucult, gunAdi,
  gunTutari, hata, kisaTarih, konumBekleyen, konumIpucu, para, tarih, useHarcirahApi,
} from './ortak';
import { cihazKonumuAl } from '../../utils/cihazKonumu';
import { GiderBolumu, GiderPenceresi } from './giderler';
import PlanPenceresi from './plan';

/**
 * Benim Harcırahım (23.10.2026) — çalışan kendi seyahatlerinde her öğünün tutarını fişiyle girer ve onaya
 * gönderir (routes/harcirah.py /harcirah-benim). Öğün hakkını (Ö/A) yönetici planlar; kapalı öğüne tutar
 * girilmez. Tavan yok, fiş zorunlu. Gönderilen seyahat değişmez; amir karar vermeden geri çekilebilir.
 * İleride mobil ekranın karşılığı — dar ekranda tek sütun.
 * 24.10.2026: fiş seçilince Claude ile okunur (fis-oku); okunan tutar öneri olarak gelir, çalışan onaylar. Okuma
 * jetonu kayıtla gider; okunan ≠ girilen, fiş tarihi, şehir ve aynı fiş işaretleri onaycılara gösterilir.
 * Şehir/tarih farkı kırmızı ve büyük gösterilir ("göze sokulur"), açıklama zorunludur (backend de denetler).
 * KVKK (24.10.2026): ilk girişte aydınlatma metni; fişin yurt dışında (Anthropic) okunması için AYRI ve isteğe
 * bağlı açık rıza onay kutusu. Rıza yoksa fiş okumaya gönderilmez (sunucu da engeller), tutar elle girilir.
 * 24.10.2026: "Ulaşım ve konaklama" bölümü (giderler.jsx). Ekran ayrımından sonra çalışan yalnız yakıt fişini girer;
 * bilet, kiralama ve oteli rezervasyon personeli girer (burada salt okunur). Bu giderleri şirket öder (geri ödenmez);
 * gönderim için ulaşım şekli seçilmiş ve rezervasyon belgeleri tamamlanmış olmalı.
 * 26.10.2026 akış: seyahati PERSONEL planlar (Seyahat planla) ve amire gönderir; plan aşamasında gün gün öğün hakkını
 * açıp kapatır. Amir onaylayınca otel / uçak gerekiyorsa rezervasyon akışı başlar (rezervasyon → finans → yönetici);
 * yemek ve yakıt bu akıştan AYRI, seyahatten sonra girilip amir → finans → yönetim onayına gönderilir.
 * 30.10.2026 ekran yenileme (kullanıcı: "ana ekran çok karışık, çok kaydırmam gerekiyor"): liste yalnız özet —
 * seyahat başına tek satır (tarih, şehir, sıradaki adım, tutar, tek eylem); ayrıntı sağdan açılan çekmecede
 * (Öğünler / Giderler / Geçmiş, eylemler altta sabit). Sekmeler sıradaki adıma göre: Yapılacak / Süreçte /
 * Tamamlanan. Kapalı öğün hakkı kırmızı. Tarih (aralıkla kesişen seyahat) ve şehir filtresi; sekme sayıları filtreye göre.
 * 03.11.2026 günlük konum doğrulaması (kullanıcı: "o ile gitti mi, dediği gün kadar orada kaldı mı"): seyahat günlerinde
 * "Konumu Doğrula" — telefonun GPS konumu seyahatin ilinin sınırları içinde mi (karar sunucuda). Doğrulanmayan gün
 * gönderimi engellemez; çalışan açıklama yazar, amir / finans karar verir.
 */
const { Text } = Typography;
const MAKS_FIS = 10 * 1024 * 1024;
const ACIKLAMA_GEREKEN = ['sehir', 'tarih'];

// --- KVKK aydınlatma + açık rıza ----------------------------------------------------------------
function AydinlatmaPenceresi({ acik, riza, onKapat, onKaydet }) {
  const [onay, setOnay] = useState(false);
  const [bekliyor, setBekliyor] = useState(false);
  useEffect(() => { if (acik) setOnay(riza?.riza === 'onay'); }, [acik, riza]);
  if (!riza) return null;
  const okundu = !!riza.aydinlatma_okundu;
  const kaydet = () => {
    setBekliyor(true);
    onKaydet(onay).finally(() => setBekliyor(false));
  };
  return (
    <Modal rootClassName="hr-modal" open={acik} onCancel={onKapat} width={720} destroyOnHidden maskClosable={false}
      title={<Space><SafetyCertificateOutlined />{riza.aydinlatma.baslik}{riza.taslak && <Tag color="orange">TASLAK — Hukuk Onayı Bekliyor</Tag>}</Space>}
      footer={[
        <Button key="k" onClick={onKapat}>Kapat</Button>,
        <Button key="o" type="primary" loading={bekliyor} onClick={kaydet}>{okundu ? 'Tercihimi Kaydet' : 'Okudum, Devam Et'}</Button>,
      ]}>
      <div style={{ maxHeight: '50vh', overflowY: 'auto', paddingRight: 8, marginBottom: 12 }}>
        {riza.aydinlatma.bolumler.map(([baslik, metin]) => (
          <div key={baslik} style={{ marginBottom: 10 }}>
            <Text strong>{baslik}</Text>
            <div style={{ fontSize: 13 }}>{metin}</div>
          </div>
        ))}
      </div>
      <div style={{ border: '1px solid rgba(128,128,128,0.35)', borderRadius: 8, padding: 12 }}>
        <Text strong>Açık Rıza (İsteğe Bağlı)</Text>
        <div style={{ marginTop: 6 }}>
          <Checkbox checked={onay} onChange={(e) => setOnay(e.target.checked)}>
            <span style={{ fontSize: 13 }}>{riza.riza_metni}</span>
          </Checkbox>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
          İşaretlemezseniz fişleriniz yurt dışına gönderilmez; tutarları elle girersiniz. Tercihinizi bu ekrandan istediğiniz zaman değiştirebilirsiniz.
        </Text>
      </div>
    </Modal>
  );
}

function TutarPenceresi({ istek, onKapat, onKaydedildi, okumaAcik }) {
  const api = useHarcirahApi('/harcirah-benim');
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [dosya, setDosya] = useState(null);
  const [okuma, setOkuma] = useState(null); // {okuma, uyarilar, jeton} | {hata}
  const [okunuyor, setOkunuyor] = useState(false);
  const [bekliyor, setBekliyor] = useState(false);
  const girilen = Form.useWatch('tutar', form);
  const aciklamaDegeri = Form.useWatch('aciklama', form) || '';
  const g = istek?.gun;
  const mevcut = g && g[`${istek.ogun}_tutar`];
  const mevcutFis = g && g[`${istek.ogun}_fis`];
  const mevcutIsaret = g ? istek.seyahat.isaretler?.[`${g.tarih}|${istek.ogun}`] || [] : [];

  useEffect(() => {
    if (!istek) return;
    setDosya(null);
    setOkuma(null);
    form.setFieldsValue({ tutar: mevcut ?? null, aciklama: mevcutFis?.aciklama || '' });
  }, [istek]); // eslint-disable-line react-hooks/exhaustive-deps

  const gonder = (tutar, aciklama) => {
    const fd = new FormData();
    fd.append('tarih', g.tarih);
    fd.append('ogun', istek.ogun);
    fd.append('tutar', tutar == null ? '' : String(tutar));
    if (tutar != null && aciklama) fd.append('aciklama', aciklama);
    if (dosya && tutar != null) fd.append('fis', dosya);
    if (dosya && okuma?.jeton) fd.append('okuma', okuma.jeton);
    setBekliyor(true);
    return api.form(`/${istek.seyahat.id}/ogun`, fd)
      .then((s) => { message.success(tutar == null ? 'Tutar silindi.' : 'Kaydedildi.'); onKaydedildi(s); })
      .catch((e) => message.error(hata(e)))
      .finally(() => setBekliyor(false));
  };

  // itiraz: çalışan "yanlış okundu" dedi → sunucu gelişmiş modelle (Opus) yeniden okur
  const okut = (k, itiraz = false) => {
    const onceki = okuma?.okuma?.toplam;
    setOkunuyor(true);
    const fd = new FormData();
    fd.append('tarih', g.tarih);
    fd.append('ogun', istek.ogun);
    fd.append('fis', k);
    if (itiraz) fd.append('itiraz', 'true');
    api.form(`/${istek.seyahat.id}/fis-oku`, fd).then((d) => {
      setOkuma(d);
      // Okunan tutar öneri: alan boşsa, önceki tutar ya da önceki okuma duruyorsa doldurulur
      const t = form.getFieldValue('tutar');
      if (d.okuma?.okunabildi && (t == null || t === mevcut || (onceki != null && t === onceki))) {
        form.setFieldsValue({ tutar: d.okuma.toplam });
      }
    }).catch((e) => {
      setOkuma({ engel: hata(e) });
      if (e?.response?.status === 409) setDosya(null);
    }).finally(() => setOkunuyor(false));
  };

  const dosyaSecildi = async (f) => {
    const k = await fisiKucult(f);
    setDosya(k);
    setOkuma(null);
    if (okumaAcik) okut(k);  // rıza yoksa fiş okumaya gönderilmez
  };

  const o = okuma?.okuma;
  // Okunan tutar: yeni fiş seçildiyse onun okuması, değilse kayıtlı fişin okuması
  const okunanToplam = dosya ? (o?.okunabildi ? o.toplam : null) : (mevcutFis?.okuma?.okunabildi ? mevcutFis.okuma.toplam : null);
  const farkli = okunanToplam != null && girilen != null && Math.abs(girilen - okunanToplam) >= 0.01;
  // Gösterilecek uyumsuzluklar: yeni fiş seçildiyse onun okuması, değilse kayıtlı öğünün işaretleri
  const uyumsuz = (dosya ? okuma?.uyarilar || [] : mevcutIsaret).filter((i) => ISARET_TANIM[i.tur]?.ciddi);
  const nedenTurleri = [...new Set(uyumsuz.map((i) => i.tur).filter((t) => ACIKLAMA_GEREKEN.includes(t)))].concat(farkli ? ['tutar'] : []);
  const aciklamaGerek = nedenTurleri.length > 0;
  const merkezAdresi = uyumsuz.some((i) => i.tur === 'sehir' && i.adres_turu === 'merkez');
  const nedenSec = (metin) => {
    const parcalar = aciklamaDegeri.split(';').map((x) => x.trim()).filter(Boolean);
    const yeni = parcalar.includes(metin) ? parcalar.filter((x) => x !== metin) : [...parcalar, metin];
    form.setFieldsValue({ aciklama: yeni.join('; ') });
  };

  const kaydet = () => form.validateFields().then((v) => {
    if (!dosya && !mevcutFis) {
      message.warning('Fiş fotoğrafı ekleyin.');
      return null;
    }
    return gonder(v.tutar, (v.aciklama || '').trim());
  }).catch(() => {});

  return (
    <Modal rootClassName="hr-modal"
      open={!!istek}
      title={g ? `${gunAdi(g.tarih)} ${tarih(g.tarih)} · ${OGUN_ADI[istek.ogun]}` : ''}
      onCancel={onKapat}
      destroyOnHidden
      okButtonProps={{ disabled: okunuyor }}
      footer={[
        mevcut != null && (
          <Button key="sil" danger icon={<DeleteOutlined />} loading={bekliyor} onClick={() => gonder(null)}
            style={{ float: 'left' }}>Tutarı Sil</Button>
        ),
        <Button key="vazgec" onClick={onKapat}>Vazgeç</Button>,
        <Button key="kaydet" type="primary" loading={bekliyor} disabled={okunuyor} onClick={kaydet}>Kaydet</Button>,
      ].filter(Boolean)}
    >
      <Form form={form} layout="vertical">
        {okunuyor && <Alert type="info" showIcon icon={<LoadingOutlined />} style={{ marginBottom: 12 }} message="Fiş okunuyor…" />}
        {!okumaAcik && dosya && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Otomatik okuma kapalı (açık rıza verilmedi); tutarı fişe bakarak girin." />}
        {!okunuyor && o && !o.okunabildi && o.kademe !== 'itiraz' && dosya && !o.hata?.includes('yapılandırılmamış') && !o.hata?.includes('HEIC') && (
          <Button size="small" style={{ marginBottom: 12 }} onClick={() => okut(dosya, true)}>Gelişmiş Modelle Tekrar Oku</Button>
        )}
        {okuma?.engel && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={okuma.engel} />}
        {o && !o.okunabildi && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={o.hata || `Fiş okunamadı${o.not ? `: ${o.not}` : ''}; tutarı elle girin.`} />}
        {o?.okunabildi && (
          <Alert type="success" showIcon icon={<ScanOutlined />} style={{ marginBottom: 12 }}
            message={(
              <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                <span>Fişten okundu: {para(o.toplam)}</span>
                {o.kademe === 'ilk' && !okunuyor && (
                  <Button size="small" type="link" style={{ padding: 0 }} onClick={() => okut(dosya, true)}>Yanlış Mı Okundu? Tekrar Oku</Button>
                )}
                {o.kademe !== 'ilk' && <Tag color="blue">Gelişmiş Modelle Okundu</Tag>}
              </Space>
            )}
            description={(
              <div style={{ fontSize: 12 }}>
                {[o.tarih && `${tarih(o.tarih)}${o.saat ? ` ${o.saat}` : ''}`, o.isletme, [o.ilce, o.il].filter(Boolean).join(' / ')].filter(Boolean).join(' · ')}
              </div>
            )} />
        )}
        {uyumsuz.length > 0 && (
          <Alert type="error" showIcon style={{ marginBottom: 12, borderWidth: 2 }}
            message={<b style={{ fontSize: 15 }}>Fiş bu öğünle uyuşmuyor</b>}
            description={(
              <div>
                {uyumsuz.map((i) => (
                  <div key={i.tur} style={{ fontSize: 14, margin: '4px 0' }}>
                    <Tag color="error" icon={ISARET_TANIM[i.tur].ikon} style={{ fontSize: 13, fontWeight: 700 }}>{ISARET_TANIM[i.tur].ad}</Tag>
                    <b>{ISARET_TANIM[i.tur].ayrinti(i)}</b>
                  </div>
                ))}
                {merkezAdresi && (
                  <div style={{ marginTop: 6 }}><b>Fişte işletmenin MERKEZ adresi okundu.</b> Yemek seyahat ilindeki şubede yendiyse aşağıdan ilk nedeni seçin.</div>
                )}
                {aciklamaGerek && <div style={{ marginTop: 6 }}>Kaydetmek için aşağıdan bir neden seçin ya da kısa bir açıklama yazın; onaycılar görecek.</div>}
              </div>
            )} />
        )}
        <Form.Item name="tutar" label="Ödediğiniz Tutar"
          rules={[{ required: true, message: 'Tutar girin' },
            { validator: (_, v) => (v == null || v > 0 ? Promise.resolve() : Promise.reject(new Error('Tutar 0’dan büyük olmalı'))) }]}>
          <InputNumber {...inputNumberMoneyProps} min={0} precision={2} suffix="₺" style={{ width: '100%' }}
            size="large" autoFocus inputMode="decimal" />
        </Form.Item>
        {farkli && (
          <Alert type="warning" showIcon style={{ marginTop: -12, marginBottom: 12 }}
            message={<span><b>TUTAR FARKLI</b> — fişten {para(okunanToplam)} okundu, siz {para(girilen)} girdiniz. Açıklama gerekli; onaycılar bu farkı görecek.</span>} />
        )}
        {aciklamaGerek && (
          <Form.Item label="Açıklama (Zorunlu)" required style={{ marginBottom: 12 }}>
            {nedenTurleri.map((t) => (
              <div key={t} style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>{ISARET_TANIM[t].ad}:</Text>
                <Space size={[4, 4]} wrap style={{ display: 'inline-flex' }}>
                  {NEDEN_ONERILERI[t].map((n) => (
                    <Tag.CheckableTag key={n} checked={aciklamaDegeri.split(';').map((x) => x.trim()).includes(n)}
                      onChange={() => nedenSec(n)} style={{ border: '1px solid rgba(128,128,128,0.35)', whiteSpace: 'normal' }}>
                      {n}
                    </Tag.CheckableTag>
                  ))}
                </Space>
              </div>
            ))}
            <Form.Item name="aciklama" noStyle rules={[{ required: true, min: 3, message: 'Bir neden seçin ya da açıklama yazın' }]}>
              <Input.TextArea rows={2} maxLength={300} showCount placeholder="Neden seçin ya da kendiniz yazın" />
            </Form.Item>
          </Form.Item>
        )}
        <Form.Item label="Fiş / Fatura Fotoğrafı" required
          extra={mevcutFis ? 'Yeni fiş seçmezseniz kayıtlı fiş kalır.' : 'JPG, PNG, HEIC ya da PDF; en fazla 10 MB.'}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {mevcutFis && (
              <Space>
                <Text type="secondary">Kayıtlı Fiş:</Text>
                <FisDugmesi seyahatId={istek.seyahat.id} gunTarihi={g.tarih} ogun={istek.ogun} fis={mevcutFis} etiket="Göster" />
              </Space>
            )}
            <Upload
              accept="image/*,application/pdf"
              maxCount={1}
              fileList={dosya ? [{ uid: '1', name: dosya.name, status: 'done' }] : []}
              beforeUpload={(f) => {
                if (f.size > MAKS_FIS) {
                  message.error('Dosya 10 MB’tan büyük.');
                  return Upload.LIST_IGNORE;
                }
                dosyaSecildi(f);
                return false;
              }}
              onRemove={() => { setDosya(null); setOkuma(null); }}
            >
              <Button icon={<CameraOutlined />} size="large" block>
                {mevcutFis ? 'Fişi Değiştir' : `Fiş Fotoğrafı Çek / Seç${okumaAcik ? ' (Tutar Okunur)' : ''}`}
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

const GUN_TAM = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const gunTam = (t) => GUN_TAM[(dayjs(t).day() + 6) % 7];
const gunAy = (t) => dayjs(t).format('DD.MM');
const yemekAcik = (s) => s.duzenlenebilir && s.plan_onayli;  // yemek + yakıt girişi: plan onaylı, harcırah taslak / geri
const basladi = (s) => s.baslangic <= dayjs().format('YYYY-MM-DD');
const sonIslem = (s, islemler) => [...s.gecmis].reverse().find((x) => islemler.includes(x.islem));
const REZ_BEKLENEN = {
  amir: 'amirin plan onayı', fiyat: 'rezervasyon personelinin fiyatları', finans_onay: 'finansın fiyat onayı',
  islem: 'rezervasyon personelinin ödemesi', finans_kontrol: 'finansın harcama kontrolü', yonetici: 'yöneticinin onayı',
};
const HARCIRAH_BEKLENEN = { amir: 'amirin onayı', finans: 'finansın onayı', son: 'son onay' };

/**
 * Seyahatin sıradaki adımı (30.10.2026 ekran yenileme): hangi sekmede durduğu, tonu (hata / eylem / bekle / tamam),
 * tek cümlelik metni ve listeden yapılabilecek tek eylemi. Öncelik: geri gönderilen → plan → yemek girişi (seyahat
 * başladıysa) → başkasında bekleyen → tamamlanan.
 */
function sonrakiAdim(s) {
  // Konum yalnız o gün doğrulanabilir; bu yüzden her şeyden önce
  if (s.konum?.bugun) {
    const o = s.konum.gunler[s.konum.bugun];
    return { grup: 'yapilacak', ton: 'eylem', eylem: 'konum', eylemAd: 'Konumu Doğrula', metin: o?.deneme
      ? `Bugünün konumu doğrulanamadı — ${s.il_ad} sınırları içindeyken yeniden deneyin`
      : `Bugünün konumunu doğrulayın (${s.il_ad})` };
  }
  if (s.durum === 'geri') {
    return { grup: 'yapilacak', ton: 'hata', metin: 'Harcırah geri gönderildi — düzeltip yeniden gönderin',
      not: sonIslem(s, ['geri_gonder']), eylem: 'ac', eylemAd: 'Düzelt' };
  }
  if (s.plan_duzenlenebilir) {
    const g = sonIslem(s, ['rez_geri', 'plan_gonder', 'olustur']);
    if (g?.islem === 'rez_geri') {
      return { grup: 'yapilacak', ton: 'hata', metin: 'Plan size geri döndü — düzeltip yeniden amire gönderin', not: g, eylem: 'ac', eylemAd: 'Düzelt' };
    }
    return { grup: 'yapilacak', ton: 'eylem', metin: 'Plan hazır mı? Öğün haklarını kontrol edip amire gönderin', eylem: 'plan_gonder', eylemAd: 'Amire Gönder' };
  }
  if (yemekAcik(s) && basladi(s)) {
    const eksik = s.acik_ogun - s.girilen_ogun;
    const y = { grup: 'yapilacak', ton: 'eylem', eylem: 'ac', eylemAd: 'Tutar Gir' };
    if (!s.girilen_ogun) return { ...y, metin: 'Yemek tutarlarını fişleriyle girin' };
    if (eksik > 0) return { ...y, metin: `${eksik} öğünün tutarı eksik — girin ya da böyle onaya gönderin` };
    if (!s.ulasim) return { ...y, metin: 'Ulaşım şeklini seçip harcırahı onaya gönderin', eylemAd: 'Aç' };
    const konumsuz = konumBekleyen(s).length;
    if (konumsuz) return { ...y, metin: `${konumsuz} günün konumu doğrulanmadı — nedenini açıklayın`, eylemAd: 'Aç' };
    return { ...y, metin: 'Bütün öğünler girildi — harcırahı onaya gönderin', eylem: 'gonder', eylemAd: 'Onaya Gönder' };
  }
  if (HARCIRAH_BEKLENEN[s.durum]) return { grup: 'surecte', ton: 'bekle', metin: `Harcırah gönderildi · ${HARCIRAH_BEKLENEN[s.durum]} bekleniyor` };
  if (REZ_BEKLENEN[s.rez_durum]) return { grup: 'surecte', ton: 'bekle', metin: `${REZ_BEKLENEN[s.rez_durum]} bekleniyor` };
  if (yemekAcik(s)) return { grup: 'surecte', ton: 'bekle', metin: `${tarih(s.baslangic)} günü başlıyor; yemekleri seyahatte girersiniz` };
  if (s.durum === 'onaylandi') return { grup: 'tamam', ton: 'tamam', metin: `Harcırah onaylandı · ${para(s.toplam)}` };
  return { grup: 'surecte', ton: 'bekle', metin: s.durum_adi };
}
const TON = {
  hata: { ikon: <ExclamationCircleFilled />, alert: 'error' },
  eylem: { ikon: <RightCircleFilled />, alert: 'info' },
  bekle: { ikon: <ClockCircleOutlined />, alert: 'info' },
  tamam: { ikon: <CheckCircleFilled />, alert: 'success' },
};
const TON_SIRA = { hata: 0, eylem: 1, bekle: 2, tamam: 3 };
const ARALIKLAR = [
  { label: 'Bu Ay', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  { label: 'Geçen Ay', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: 'Son 3 Ay', value: [dayjs().subtract(2, 'month').startOf('month'), dayjs().endOf('month')] },
  { label: 'Bu Yıl', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
];
// ton: antd renk ailesi (colorPrimary / colorWarning / colorSuccess …); boşsa nötr
const SEKMELER = [
  { k: 'yapilacak', ad: 'Yapılacak', alt: 'Sizden İşlem Bekliyor', ton: 'Primary', ikon: <FormOutlined /> },
  { k: 'surecte', ad: 'Süreçte', alt: 'Onayda Ya Da Yaklaşan', ton: 'Warning', ikon: <HourglassOutlined /> },
  { k: 'tamam', ad: 'Tamamlanan', alt: 'Harcırahı Onaylandı', ton: 'Success', ikon: <CheckCircleOutlined /> },
  { k: 'tumu', ad: 'Tümü', alt: 'Bütün Seyahatler', ikon: <AppstoreOutlined /> },
];
// Yapılacaklar önce acil (geri gönderilen), sonra en yakın tarih; süreçte en yakın; tamamlanan en yeni önce
const SIRALA = {
  yapilacak: (a, b) => (TON_SIRA[a.adim.ton] - TON_SIRA[b.adim.ton]) || a.s.baslangic.localeCompare(b.s.baslangic),
  surecte: (a, b) => a.s.baslangic.localeCompare(b.s.baslangic),
  tamam: (a, b) => b.s.baslangic.localeCompare(a.s.baslangic),
};

// Plan aşamasında öğün hakkı: tıklayınca açılır / kapanır; kapalı öğün kırmızı (30.10.2026 kullanıcı isteği)
function HakKarosu({ s, g, ogun, onHak }) {
  const acik = !!g[ogun];
  return (
    <button type="button" className={`hb-karo ${acik ? 'hb-karo-hak' : 'hb-karo-kapali'} hb-karo-dugme`} onClick={() => onHak(s, g, ogun, !acik)}
      aria-label={`${OGUN_ADI[ogun]} hakkını ${acik ? 'kapat' : 'aç'}`} aria-pressed={acik}>
      {acik ? <><CheckCircleOutlined /> {OGUN_ADI[ogun]} Hakkı Var</> : <><CloseCircleOutlined /> {OGUN_ADI[ogun]} Harcırahı Yok</>}
      <span className="hb-hak-ipucu">{acik ? 'Kapat' : 'Aç'}</span>
    </button>
  );
}

// Öğün kutusu: üç halde de aynı boy — hak yok / boş / dolu — böylece satırlar hizalı kalır
function OgunKarosu({ s, g, ogun, onGir }) {
  if (!g[ogun]) {
    return <div className="hb-karo hb-karo-kapali"><CloseCircleOutlined /> Harcırah Yok</div>;
  }
  const t = g[`${ogun}_tutar`];
  if (t == null) {
    return yemekAcik(s)
      ? <button type="button" className="hb-karo hb-karo-bos" onClick={() => onGir(s, g, ogun)}><PlusOutlined /> Tutar Ve Fiş Ekle</button>
      : <div className="hb-karo hb-karo-yok">Girilmedi</div>;
  }
  const isaretler = s.isaretler?.[`${g.tarih}|${ogun}`];
  const duzenle = yemekAcik(s) ? () => onGir(s, g, ogun) : undefined;
  return (
    <div className={`hb-karo hb-karo-dolu${ciddiMi(isaretler) ? ' hb-karo-isaretli' : ''}${duzenle ? ' hb-tikla' : ''}`}
      role={duzenle ? 'button' : undefined} tabIndex={duzenle ? 0 : undefined} onClick={duzenle}
      onKeyDown={duzenle ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); duzenle(); } } : undefined}
      aria-label={duzenle ? `${OGUN_ADI[ogun]} ${para(t)} — düzenle` : undefined}>
      <div className="hb-karo-resim" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <FisKucuk seyahatId={s.id} gunTarihi={g.tarih} ogun={ogun} fis={g[`${ogun}_fis`]} />
      </div>
      <div className="hb-karo-icerik">
        <div className="hb-karo-tutar">{para(t)}</div>
        {isaretler?.length ? <IsaretEtiketleri isaretler={isaretler} kisa /> : <Text type="secondary" style={{ fontSize: 12 }}>Fiş Eklendi</Text>}
      </div>
      {duzenle && <EditOutlined className="hb-karo-duzenle" />}
    </div>
  );
}

function GunSatiri({ s, g, onGir, onHak, onKonumAciklama }) {
  // Ayrıntı yalnız anlamlı ayrıntısı olan işaretler için (okunamadı kutudaki etiketle yeterli)
  const ayrinti = ['ogle', 'aksam'].map((o) => [o, (s.isaretler?.[`${g.tarih}|${o}`] || []).filter((i) => i.tur !== 'okunamadi')])
    .filter(([, l]) => l.length);
  const hs = (dayjs(g.tarih).day() + 6) % 7 >= 5;
  const konum = s.konum?.gunler?.[g.tarih];
  // Açıklama satırı: geçmiş gün doğrulanmadıysa; bugün yalnız başarısız denemeden sonra (önce doğrulamayı denesin)
  const konumSatiri = konum && (konum.durum === 'eksik' || (konum.durum === 'bugun' && (konum.deneme || konum.aciklama)));
  return (
    <div className="hb-satir">
      <div className="hb-h-gun">
        <b>{gunTam(g.tarih)}{hs && <span className="hb-hs">Hafta Sonu</span>}</b>
        <span>{tarih(g.tarih)}</span>
        {KONUM_TANIM[konum?.durum] && <span className="hb-h-konum-etiket"><KonumEtiketi o={konum} /></span>}
      </div>
      <div className="hb-h-ogle">{onHak ? <HakKarosu s={s} g={g} ogun="ogle" onHak={onHak} /> : <OgunKarosu s={s} g={g} ogun="ogle" onGir={onGir} />}</div>
      <div className="hb-h-aksam">{onHak ? <HakKarosu s={s} g={g} ogun="aksam" onHak={onHak} /> : <OgunKarosu s={s} g={g} ogun="aksam" onGir={onGir} />}</div>
      <div className="hb-h-toplam">{gunTutari(g) ? para(gunTutari(g)) : <Text type="secondary">—</Text>}</div>
      {ayrinti.length > 0 && (
        <div className="hb-h-ayrinti">
          {ayrinti.map(([o, l]) => (
            <div key={o} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <Text type="secondary" style={{ fontSize: 12, minWidth: 44, paddingTop: 5 }}>{OGUN_ADI[o]}:</Text>
              <div style={{ flex: 1, minWidth: 0 }}><IsaretEtiketleri isaretler={l} /></div>
            </div>
          ))}
        </div>
      )}
      {konumSatiri && (
        <div className={`hb-h-konum${konum.aciklama ? '' : ' hb-h-konum-eksik'}`}>
          <EnvironmentOutlined className="hb-h-konum-ikon" />
          <div className="hb-h-konum-metin">
            <span>{konum.mesaj ? `Son deneme ${konum.saat}: ${konum.mesaj}` : 'Bu gün konum doğrulaması yapılmadı.'}</span>
            {konum.aciklama
              ? <span className="hb-h-konum-aciklama">Açıklamanız: “{konum.aciklama}”</span>
              : <span className="hb-h-konum-aciklama">
                {konum.durum === 'bugun' ? 'İl sınırları içindeyken yeniden deneyin; olmazsa nedenini açıklayın.'
                  : 'Göndermeden önce nedenini kısaca açıklayın.'}
              </span>}
          </div>
          {onKonumAciklama && (
            <Button size="small" icon={<EditOutlined />} onClick={() => onKonumAciklama(g.tarih, konum)}>
              {konum.aciklama ? 'Düzenle' : 'Açıklama Yaz'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Doğrulanmayan güne açıklama (routes/harcirah.py /konum-aciklama); boş kaydedilirse açıklama silinir. */
function KonumAciklamaPenceresi({ istek, onKapat, onKaydet }) {
  const [metin, setMetin] = useState('');
  const [bekle, setBekle] = useState(false);
  useEffect(() => { if (istek) setMetin(istek.konum.aciklama || ''); }, [istek]);
  const kaydet = async () => {
    setBekle(true);
    try { await onKaydet(metin); } finally { setBekle(false); }
  };
  return (
    <Modal open={!!istek} title="Konum Açıklaması" className="hr-modal" onCancel={onKapat} onOk={kaydet} confirmLoading={bekle}
      okText="Kaydet" cancelText="Vazgeç">
      {istek && (
        <>
          <p style={{ marginTop: 0 }}>
            <b>{gunTam(istek.tarih)} {tarih(istek.tarih)}</b> günü konumunuz doğrulanmadı. Nedenini kısaca yazın; amir ve finans
            bu açıklamayla karar verir.
          </p>
          {istek.konum.mesaj && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={konumIpucu(istek.konum)} />}
          <Input.TextArea value={metin} onChange={(e) => setMetin(e.target.value)} maxLength={250} showCount autoFocus
            autoSize={{ minRows: 3, maxRows: 6 }} placeholder="Ör. Telefonumun şarjı bitti; gün boyu şantiyedeydim." />
        </>
      )}
    </Modal>
  );
}

const isaretSayisi = (s) => Object.keys(s.isaretler || {}).length + Object.keys(s.gider_isaretleri || {}).length;
const ogunOzeti = (s) => (s.plan_onayli ? `${s.girilen_ogun}/${s.acik_ogun} Öğün Girildi` : `${s.acik_ogun} Öğün Hakkı`);

// Liste satırı: tarih başta, şehir + sıradaki adım, tutar, tek eylem; satıra tıklayınca ayrıntı çekmecesi açılır
function SeyahatSatiri({ s, adim, onAc, onEylem, bekle }) {
  const isaret = isaretSayisi(s);
  const ac = () => onAc(s);
  const tekGun = s.baslangic === s.bitis;
  return (
    <div className={`sy-satir sy-${adim.ton}${adim.eylem ? '' : ' sy-eylemsiz'}`} role="button" tabIndex={0} onClick={ac}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ac(); } }}
      aria-label={`${s.il_ad}, ${tarih(s.baslangic)}–${tarih(s.bitis)}: ${adim.metin}. Ayrıntıyı aç`}>
      <div className="sy-tarih">
        <div className="sy-tarih-ust">{gunAy(s.baslangic)}{tekGun ? '' : ` → ${gunAy(s.bitis)}`}</div>
        <div className="sy-tarih-alt">{gunAdi(s.baslangic)}{tekGun ? '' : `–${gunAdi(s.bitis)}`} · {s.gunler.length} Gün</div>
      </div>
      <div className="sy-orta">
        <div className="sy-baslik">
          <span className="sy-il">{s.il_ad}</span>
          <Tag style={{ marginInlineEnd: 0 }}>{s.seyahat_turu_adi}</Tag>
          {isaret > 0 && <Tag color="error" icon={<WarningFilled />} style={{ marginInlineEnd: 0 }}>{isaret} Kontrol İşareti</Tag>}
        </div>
        <div className="sy-adim">{TON[adim.ton].ikon}<span>{adim.metin}</span></div>
      </div>
      <div className="sy-tutar">
        {/* Plan aşamasında tutar yok: öğün hakkı sayısı */}
        <div className="sy-tutar-deger">{s.plan_onayli ? para(s.toplam) : `${s.acik_ogun} Öğün`}</div>
        <div className="sy-tutar-alt">{s.plan_onayli ? ogunOzeti(s) : 'Harcırah Hakkı'}</div>
      </div>
      <div className="sy-eylem">
        {adim.eylem && (
          <Button type="primary" danger={adim.ton === 'hata'} loading={bekle}
            icon={adim.eylem === 'konum' ? <EnvironmentOutlined /> : undefined}
            onClick={(e) => { e.stopPropagation(); onEylem(s, adim.eylem); }}>
            {adim.eylemAd}
          </Button>
        )}
        <RightOutlined className="sy-ok" />
      </div>
    </div>
  );
}

// Seyahat ayrıntısı: sağdan açılan çekmece (telefonda tam ekran); eylemler altta sabit
function SeyahatCekmecesi({
  s, degiskenler, onKapat, onGir, onGonder, onGeriCek, onGiderEkle, onGiderDuzenle, onHak, onPlan, onKonum, konumBekle, onKonumAciklama,
}) {
  const genis = Grid.useBreakpoint().md;
  const son = useRef(null);
  if (s) son.current = s;
  const g = s || son.current;  // kapanırken içerik boşalmasın
  const [sekme, setSekme] = useState('ogun');
  useEffect(() => { if (s) setSekme('ogun'); }, [s?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!g) return null;
  const adim = sonrakiAdim(g);
  const planda = g.plan_duzenlenebilir;
  const acikYemek = yemekAcik(g);
  const giderIsareti = Object.keys(g.gider_isaretleri || {}).length;
  const tekGun = g.baslangic === g.bitis;
  const konumEksik = konumBekleyen(g);
  const bugunKonum = g.konum?.bugun ? g.konum.gunler[g.konum.bugun] : null;
  const gonderIpucu = !g.girilen_ogun ? 'Önce en az bir öğüne tutar girin' : !g.ulasim ? 'Ulaşım şeklini seçin'
    : konumEksik.length ? `Konumu doğrulanmayan günler (${konumEksik.map(kisaTarih).join(', ')}): doğrulayın ya da nedenini açıklayın` : '';
  return (
    <Drawer open={!!s} onClose={onKapat} width={genis ? 900 : '100%'} rootClassName="hr-modal" rootStyle={degiskenler}
      title={(
        <div style={{ minWidth: 0 }}>
          <div className="sy-c-baslik"><EnvironmentOutlined /> {g.il_ad} <Tag style={{ marginInlineEnd: 0 }}>{g.seyahat_turu_adi}</Tag></div>
          <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
            {tarih(g.baslangic)}{tekGun ? '' : ` – ${tarih(g.bitis)}`} · {gunTam(g.baslangic)}{tekGun ? '' : ` – ${gunTam(g.bitis)}`} · {g.gunler.length} Gün
          </Text>
        </div>
      )}
      footer={(
        <div className="sy-c-alt-bar">
          <div className="sy-c-ozet">
            {g.plan_onayli ? <span>Yemek Harcırahı <b>{para(g.toplam)}</b></span> : <span>Öğün Hakkı <b>{g.acik_ogun}</b></span>}
            {g.plan_onayli && <span>{ogunOzeti(g)}</span>}
            {g.gider_toplam > 0 && <span>Şirket Giderleri {para(g.gider_toplam)}</span>}
          </div>
          <Space wrap>
            {planda && <Button icon={<EditOutlined />} onClick={() => onPlan(g, 'duzenle')}>Planı Düzenle</Button>}
            {planda && !g.girilen_ogun && !g.giderler.length && (
              <Button danger icon={<DeleteOutlined />} onClick={() => onPlan(g, 'sil')}>Sil</Button>
            )}
            {planda && <Button type="primary" icon={<SendOutlined />} onClick={() => onPlan(g, 'gonder')}>Amire Gönder</Button>}
            {g.rez_durum === 'amir' && <Button icon={<RollbackOutlined />} onClick={() => onPlan(g, 'geri_cek')}>Planı Geri Çek</Button>}
            {g.durum === 'amir' && <Button icon={<RollbackOutlined />} onClick={() => onGeriCek(g)}>Harcırah Gönderimini Geri Çek</Button>}
            {acikYemek && (
              <Tooltip title={gonderIpucu}>
                <Button type="primary" icon={<SendOutlined />} disabled={!!gonderIpucu}
                  onClick={() => onGonder(g)}>Harcırahı Onaya Gönder</Button>
              </Tooltip>
            )}
          </Space>
        </div>
      )}>
      {bugunKonum ? (
        <div className="hb-konum-kart">
          <span className="hb-konum-kart-ikon"><EnvironmentOutlined /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="hb-konum-kart-baslik">Bugünün Konumu</div>
            <div>Seyahat gününüz: {g.il_ad} il sınırları içindeyken konumunuzu doğrulayın. Günde bir kez yeterli.</div>
            {bugunKonum.mesaj && <div className="hb-konum-kart-son">Son deneme {bugunKonum.saat}: {bugunKonum.mesaj}</div>}
          </div>
          <Button type="primary" icon={<EnvironmentOutlined />} loading={konumBekle === g.id} onClick={() => onKonum(g)}>
            Konumu Doğrula
          </Button>
        </div>
      ) : (
        <Alert type={TON[adim.ton].alert} showIcon icon={TON[adim.ton].ikon} style={{ marginBottom: 12 }}
          message={<b>{adim.metin}</b>}
          description={adim.not ? (
            <span><b>{adim.not.kisi_adi}</b>{adim.not.vekil ? ` (test vekili, ${adim.not.yerine} yerine)` : ''}: <i>{adim.not.notu}</i></span>
          ) : null} />
      )}
      <Descriptions className="sy-c-bilgi" size="small" column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} style={{ marginBottom: 8 }}
        items={[
          { label: 'Amir', children: g.amir_adi },
          { label: 'Harcırah Tipi', children: `${g.tip} — ${TIP_ADI[g.tip]}` },
          { label: 'Ulaşım', children: g.ulasim_adi || '—' },
          ...(g.konaklama_gerekli != null ? [{ label: 'Otel', children: g.konaklama_gerekli ? 'Gerekli' : 'Gerekmiyor' }] : []),
          ...(g.konum?.beklenen ? [{ label: 'Konum', children: `${g.konum.dogrulanan}/${g.konum.beklenen} Gün Doğrulandı` }] : []),
          ...(g.plan_onayli ? [{ label: 'Harcırah', children: <Tag color={DURUM_RENK[g.durum]} style={{ marginInlineEnd: 0 }}>{g.durum_adi}</Tag> }] : []),
          ...(g.rez_notu ? [{ label: 'Rezervasyon Talebi', span: 'filled', children: g.rez_notu }] : []),
          ...(g.aciklama ? [{ label: 'Açıklama', span: 'filled', children: g.aciklama }] : []),
        ]} />
      {g.rez_durum && <div style={{ marginBottom: 12 }}><SeyahatAkisi s={g} kompakt /></div>}
      <Segmented block value={sekme} onChange={setSekme} style={{ marginBottom: 12 }} options={[
        { value: 'ogun', label: `Öğünler · ${g.acik_ogun}` },
        { value: 'gider', label: <span>Giderler · {g.giderler.length}{giderIsareti > 0 && <Badge status="error" style={{ marginLeft: 6 }} />}</span> },
        { value: 'gecmis', label: `Geçmiş · ${g.gecmis.length}` },
      ]} />
      {sekme === 'ogun' && (
        <>
          {planda
            ? <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Kutuya dokunarak öğün hakkını açıp kapatın (ör. yemeği müşteri karşılayacaksa). Kapalı öğün kırmızı görünür.</Text>
            : <IsaretBandi isaretler={g.isaretler} giderIsaretleri={g.gider_isaretleri} kime="calisan" />}
          <div className="hb-satir hb-baslik">
            <div className="hb-h-gun">Gün</div><div className="hb-h-ogle">Öğle</div>
            <div className="hb-h-aksam">Akşam</div><div className="hb-h-toplam">Toplam</div>
          </div>
          {g.gunler.map((gun) => (
            <GunSatiri key={gun.tarih} s={g} g={gun} onGir={onGir} onHak={planda ? onHak : undefined}
              onKonumAciklama={acikYemek ? (t, o) => onKonumAciklama(g, t, o) : undefined} />
          ))}
        </>
      )}
      {sekme === 'gider' && (
        <GiderBolumu s={g} rol="calisan"
          onEkle={acikYemek ? (t) => onGiderEkle(g, t) : undefined}
          onDuzenle={acikYemek ? (gd) => onGiderDuzenle(g, gd) : undefined}
          not={planda ? 'Plan onaylanınca ulaşım ve konaklama burada görünür.' : undefined} />
      )}
      {sekme === 'gecmis' && <GecmisListesi gecmis={g.gecmis} />}
    </Drawer>
  );
}

export default function BenimHarcirahim() {
  const api = useHarcirahApi('/harcirah-benim');
  const { message, modal } = App.useApp();
  const [liste, setListe] = useState(null);
  const [istek, setIstek] = useState(null);
  const [giderIstek, setGiderIstek] = useState(null);
  const [riza, setRiza] = useState(null);
  const [aydinlatmaAcik, setAydinlatmaAcik] = useState(false);
  const [suzgec, setSuzgec] = useState(null);
  const [plan, setPlan] = useState({ acik: false, seyahat: null });
  const [seciliId, setSeciliId] = useState(null);
  const [tarihSuz, setTarihSuz] = useState(null);  // [dayjs, dayjs]: aralıkla kesişen seyahatler
  const [ilSuz, setIlSuz] = useState([]);
  const [konumBekle, setKonumBekle] = useState(null);  // konumu doğrulanan seyahatin id'si
  const [konumAciklama, setKonumAciklama] = useState(null);  // { s, tarih, konum }
  const { token } = theme.useToken();

  useEffect(() => {
    api.get('/riza').then((d) => { setRiza(d); if (!d.aydinlatma_okundu) setAydinlatmaAcik(true); }).catch(() => {});
  }, [api]);

  const rizaKaydet = async (onay) => {
    try {
      let d = riza;
      if (!d.aydinlatma_okundu) d = { ...d, ...(await api.post('/riza', { tur: 'aydinlatma', karar: 'okundu', surum: d.surum })) };
      const karar = onay ? 'onay' : (d.riza === 'onay' ? 'geri_alindi' : 'ret');
      if (karar !== d.riza && !(karar === 'ret' && d.riza === 'geri_alindi')) {
        d = { ...d, ...(await api.post('/riza', { tur: 'riza', karar, surum: d.surum })) };
      }
      setRiza(d);
      setAydinlatmaAcik(false);
      message.success(onay ? 'Fiş otomatik okuma açık.' : 'Kaydedildi; tutarları elle gireceksiniz.');
    } catch (e) {
      message.error(hata(e));
    }
  };
  const okumaAcik = riza?.riza === 'onay';
  const tutarGir = (sy, g, ogun) => (riza?.aydinlatma_okundu ? setIstek({ seyahat: sy, gun: g, ogun }) : setAydinlatmaAcik(true));
  const giderEkle = (sy, tur) => (riza?.aydinlatma_okundu ? setGiderIstek({ seyahat: sy, tur }) : setAydinlatmaAcik(true));
  const giderDuzenle = (sy, gd) => setGiderIstek({ seyahat: sy, tur: gd.tur, gider: gd });

  const yukle = useCallback(() => api.get('').then((d) => setListe(d.seyahatler))
    .catch((e) => { message.error(hata(e)); setListe([]); }), [api, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const guncelle = (s) => setListe((l) => (l.some((x) => x.id === s.id) ? l.map((x) => (x.id === s.id ? s : x)) : [s, ...l]));
  const hakDegistir = (sy, g, ogun, deger) => api.put(`/${sy.id}/ogun`, { tarih: g.tarih, ogun, deger })
    .then(guncelle).catch((e) => message.error(hata(e)));
  const planIslem = (sy, islem) => {
    if (islem === 'duzenle') { setPlan({ acik: true, seyahat: sy }); return; }
    const tanim = {
      gonder: ['Plan amire gönderilsin mi?', `${sy.amir_adi} onaylayınca ${sy.rez_gerekli ? 'rezervasyon personeli otel / bilet işlemlerine başlar' : 'seyahat onaylanmış olur'}.`, 'Gönder', () => api.post(`/${sy.id}/plan-gonder`)],
      geri_cek: ['Plan geri çekilsin mi?', 'Plan size döner; düzenleyip yeniden gönderebilirsiniz.', 'Geri Çek', () => api.post(`/${sy.id}/plan-geri-cek`)],
      sil: ['Plan silinsin mi?', `${sy.il_ad} · ${tarih(sy.baslangic)}–${tarih(sy.bitis)}`, 'Sil', () => api.del(`/${sy.id}`)],
    }[islem];
    modal.confirm({
      title: tanim[0], content: tanim[1], okText: tanim[2], cancelText: 'Vazgeç', className: 'hr-modal',
      okButtonProps: islem === 'sil' ? { danger: true } : undefined,
      onOk: () => tanim[3]().then((y) => {
        if (islem === 'sil') { setListe((l) => l.filter((x) => x.id !== sy.id)); setSeciliId(null); } else guncelle(y);
        message.success({ gonder: 'Plan amire gönderildi.', geri_cek: 'Plan geri çekildi.', sil: 'Plan silindi.' }[islem]);
      }).catch((e) => { message.error(hata(e)); yukle(); }),
    });
  };

  const gonder = (s) => modal.confirm({
    title: 'Harcırah onaya gönderilsin mi?', className: 'hr-modal',
    content: (
      <div>
        <p>{s.il_ad} · {tarih(s.baslangic)}–{tarih(s.bitis)}</p>
        <p style={{ marginBottom: 4 }}>Yemek Harcırahı (Size Ödenir): <b>{para(s.toplam)}</b></p>
        <p>Yakıt (Şirket Öder): <b>{para(s.giderler.filter((g) => g.tur === 'yakit').reduce((a, g) => a + g.tutar, 0))}</b></p>
        {s.girilen_ogun < s.acik_ogun && (
          <Alert type="warning" showIcon message={`${s.acik_ogun - s.girilen_ogun} öğüne tutar girilmedi; bu öğünler ödenmez.`} />
        )}
        <p style={{ marginTop: 8 }}>Gönderdikten sonra amir karar verene kadar geri çekebilirsiniz.</p>
      </div>
    ),
    okText: 'Gönder', cancelText: 'Vazgeç',
    onOk: () => api.post(`/${s.id}/gonder`).then((y) => { guncelle(y); message.success('Onaya gönderildi.'); })
      .catch((e) => { message.error(hata(e)); yukle(); }),
  });

  const geriCek = (s) => modal.confirm({
    title: 'Gönderim geri çekilsin mi?', content: 'Seyahat taslağa döner; tutarları düzeltip yeniden gönderebilirsiniz.',
    okText: 'Geri Çek', cancelText: 'Vazgeç', className: 'hr-modal',
    onOk: () => api.post(`/${s.id}/geri-cek`).then((y) => { guncelle(y); message.success('Geri çekildi.'); })
      .catch((e) => { message.error(hata(e)); yukle(); }),
  });

  // Günlük konum: telefonun GPS'i alınır, karar sunucuda (il sınırı). Reddedilen deneme de kaydedilir; gün içinde yeniden denenebilir.
  const konumDogrula = async (s) => {
    if (!riza?.aydinlatma_okundu) { setAydinlatmaAcik(true); return; }
    setKonumBekle(s.id);
    try {
      const d = await api.post(`/${s.id}/konum`, await cihazKonumuAl());
      guncelle(d.seyahat);
      if (d.sonuc.gecerli) message.success(d.sonuc.mesaj);
      else modal.warning({ title: 'Konum Doğrulanamadı', content: d.sonuc.mesaj, okText: 'Tamam', className: 'hr-modal' });
    } catch (e) {
      message.error(hata(e));
    } finally {
      setKonumBekle(null);
    }
  };
  const konumAciklamaKaydet = (metin) => api.put(`/${konumAciklama.s.id}/konum-aciklama`, { tarih: konumAciklama.tarih, aciklama: metin })
    .then((y) => { guncelle(y); setKonumAciklama(null); message.success(metin.trim() ? 'Açıklama kaydedildi.' : 'Açıklama silindi.'); })
    .catch((e) => message.error(hata(e)));

  const eylem = (s, e) => {
    if (e === 'konum') konumDogrula(s);
    else if (e === 'plan_gonder') planIslem(s, 'gonder');
    else if (e === 'gonder') gonder(s);
    else setSeciliId(s.id);
  };

  if (!liste) return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;

  const [tBas, tBit] = tarihSuz ? tarihSuz.map((d) => d.format('YYYY-MM-DD')) : [];
  const suzulmus = liste.filter((s) => (!tarihSuz || (s.baslangic <= tBit && s.bitis >= tBas))
    && (!ilSuz.length || ilSuz.includes(s.il_no)));
  const filtreli = !!tarihSuz || ilSuz.length > 0;
  const temizle = () => { setTarihSuz(null); setIlSuz([]); };
  const ilSayisi = {};
  liste.forEach((s) => { ilSayisi[s.il_no] = { ad: s.il_ad, n: (ilSayisi[s.il_no]?.n || 0) + 1 }; });
  const ilSecenekleri = Object.entries(ilSayisi).sort(([, a], [, b]) => a.ad.localeCompare(b.ad, 'tr'))
    .map(([no, x]) => ({ value: Number(no), label: x.ad, ek: x.n }));
  const kalemler = suzulmus.map((s) => ({ s, adim: sonrakiAdim(s) }));
  const grubu = (k) => kalemler.filter((x) => x.adim.grup === k).sort(SIRALA[k]);
  const sayi = { yapilacak: grubu('yapilacak').length, surecte: grubu('surecte').length, tamam: grubu('tamam').length, tumu: suzulmus.length };
  const etkin = suzgec || (sayi.yapilacak ? 'yapilacak' : sayi.surecte ? 'surecte' : 'tumu');
  const bolumler = etkin === 'tumu' ? ['yapilacak', 'surecte', 'tamam'] : [etkin];
  const secili = liste.find((x) => x.id === seciliId) || null;
  const degiskenler = {
    '--hb-yuzey': token.colorBgContainer, '--hb-dolgu': token.colorFillQuaternary, '--hb-dolgu2': token.colorFillTertiary,
    '--hb-kenar': token.colorBorderSecondary, '--hb-kenar2': token.colorBorder, '--hb-birincil': token.colorPrimary,
    '--hb-hata': token.colorError, '--hb-hata-zemin': token.colorErrorBg, '--hb-hata-kenar': token.colorErrorBorder,
    '--hb-ikincil': token.colorTextSecondary, '--hb-yazi': token.colorText, '--hb-baslik-zemin': token.colorFillAlter,
    '--sy-k-hata': token.colorError, '--sy-k-eylem': token.colorPrimary, '--sy-k-bekle': token.colorTextQuaternary,
    '--sy-k-tamam': token.colorSuccess, '--sy-golge': token.boxShadowTertiary, '--hb-soluk': token.colorTextQuaternary,
    '--hb-birincil-zemin': token.colorPrimaryBg,
  };
  const sekmeRengi = (ton) => (ton ? {
    '--t': token[`color${ton}`], '--t-sayi': token[`color${ton}`], '--t-zemin': token[`color${ton}Bg`],
    '--t-zemin2': token[`color${ton}BgHover`], '--t-kenar': token[`color${ton}Border`],
  } : {
    '--t': token.colorTextSecondary, '--t-sayi': token.colorText, '--t-zemin': token.colorFillQuaternary,
    '--t-zemin2': token.colorFillSecondary, '--t-kenar': token.colorBorder,
  });
  const nasil = (
    <ol style={{ margin: 0, paddingLeft: 18, maxWidth: 360, fontSize: 13 }}>
      <li>Seyahatinizi planlayın; gün gün öğün hakkını ayarlayıp amire gönderin.</li>
      <li>Otel / uçak gerekiyorsa rezervasyon personeli ayarlar, finans onaylar.</li>
      <li>Seyahatte yemek tutarlarını fişleriyle, yakıtı belgesiyle girin.</li>
      <li>Harcırahı onaya gönderin: amir → finans → son onay.</li>
    </ol>
  );

  return (
    <div className="hb-sayfa" style={degiskenler}>
      <style>{`
        .hb-satir { display: grid; grid-template-columns: 150px minmax(0, 1fr) minmax(0, 1fr) 110px; column-gap: 12px; row-gap: 6px;
                    align-items: stretch; padding: 10px 0; border-bottom: 1px solid var(--hb-kenar); }
        .hb-baslik { padding: 0 0 6px; font-size: 12px; color: var(--hb-ikincil); text-transform: uppercase; letter-spacing: .04em; }
        .hb-h-gun { grid-row: 1; grid-column: 1; display: flex; flex-direction: column; justify-content: center; }
        .hb-h-gun > span { font-size: 12px; color: var(--hb-ikincil); }
        .hb-hs { margin-left: 6px; font-size: 11px; font-weight: 400; color: var(--hb-ikincil); border: 1px solid var(--hb-kenar2);
                 border-radius: 4px; padding: 0 4px; white-space: nowrap; }
        .hb-h-ogle { grid-row: 1; grid-column: 2; }
        .hb-h-aksam { grid-row: 1; grid-column: 3; }
        .hb-h-toplam { grid-row: 1; grid-column: 4; display: flex; align-items: center; justify-content: flex-end;
                       font-weight: 600; font-variant-numeric: tabular-nums; }
        .hb-h-ayrinti { grid-row: 2; grid-column: 2 / 4; }
        .hb-h-konum-etiket { margin-top: 4px; }
        .hb-h-konum { grid-column: 1 / -1; display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 10px;
                      background: var(--hb-dolgu); border: 1px solid var(--hb-kenar); font-size: 13px; }
        .hb-h-konum-eksik { background: var(--hb-hata-zemin); border-color: var(--hb-hata-kenar); }
        .hb-h-konum-ikon { font-size: 16px; color: var(--hb-ikincil); }
        .hb-h-konum-eksik .hb-h-konum-ikon { color: var(--hb-hata); }
        .hb-h-konum-metin { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .hb-h-konum-aciklama { color: var(--hb-ikincil); }
        .hb-h-konum-eksik .hb-h-konum-aciklama { color: var(--hb-hata); font-weight: 500; }
        .hb-konum-kart { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 14px 16px; margin-bottom: 12px;
                         border: 1px solid var(--hb-birincil); border-radius: 12px; background: var(--hb-birincil-zemin); }
        .hb-konum-kart-ikon { width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center; font-size: 20px;
                              color: var(--hb-birincil); background: var(--hb-yuzey); flex: none; }
        .hb-konum-kart-baslik { font-weight: 600; font-size: 15px; }
        .hb-konum-kart-son { margin-top: 4px; color: var(--hb-hata); font-weight: 500; }
        .hb-karo { box-sizing: border-box; width: 100%; height: 100%; min-height: 64px; border-radius: 10px; padding: 8px 10px;
                   display: flex; align-items: center; gap: 10px; border: 1px solid transparent; }
        .hb-karo-yok { justify-content: center; gap: 6px; background: var(--hb-dolgu); color: var(--hb-ikincil); font-size: 12px; }
        .hb-karo-kapali { justify-content: center; gap: 6px; background: var(--hb-hata-zemin); border-color: var(--hb-hata-kenar);
                          color: var(--hb-hata); font-size: 13px; font-weight: 500; }
        .hb-karo-hak { justify-content: center; gap: 6px; border-color: var(--hb-birincil); color: var(--hb-birincil); font-size: 13px; background: transparent; }
        .hb-karo-dugme { cursor: pointer; font: inherit; position: relative; }
        .hb-karo-dugme:hover, .hb-karo-dugme:focus-visible { border-color: var(--hb-birincil); outline: none; }
        .hb-karo-kapali.hb-karo-dugme:hover, .hb-karo-kapali.hb-karo-dugme:focus-visible { border-color: var(--hb-hata); }
        .hb-hak-ipucu { position: absolute; top: 4px; right: 8px; font-size: 11px; color: var(--hb-ikincil); }
        .hb-karo-bos { justify-content: center; gap: 6px; border: 1px dashed var(--hb-kenar2); background: transparent;
                       color: var(--hb-birincil); cursor: pointer; font: inherit; font-size: 13px; }
        .hb-karo-bos:hover, .hb-karo-bos:focus-visible { border-color: var(--hb-birincil); background: var(--hb-dolgu); outline: none; }
        .hb-karo-dolu { position: relative; border-color: var(--hb-kenar); }
        .hb-karo-dolu.hb-tikla { cursor: pointer; }
        .hb-karo-dolu.hb-tikla:hover, .hb-karo-dolu.hb-tikla:focus-visible { border-color: var(--hb-birincil); outline: none; }
        .hb-karo-icerik { flex: 1; min-width: 0; padding-right: 16px; }
        .hb-karo-duzenle { position: absolute; top: 8px; right: 8px; font-size: 12px; color: var(--hb-ikincil); }
        .hb-karo-isaretli { border: 2px solid var(--hb-hata); background: var(--hb-hata-zemin); }
        .hb-karo-tutar { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; }

        .sy-ust { display: flex; justify-content: space-between; align-items: stretch; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
        /* Sekme kartı: üstte ad + durum simgesi, altta büyük sayı + açıklama; seçili kart kendi renginde zemin ve alt çizgi alır */
        .sy-sekmeler { flex: 1 1 560px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
        .sy-sekme { position: relative; overflow: hidden; text-align: left; font: inherit; cursor: pointer; color: var(--hb-yazi);
                    background: var(--hb-yuzey); border: 1px solid var(--hb-kenar); border-radius: 12px; padding: 12px 14px 14px;
                    display: grid; grid-template-columns: auto minmax(0, 1fr) auto; grid-template-areas: "ad ad ikon" "sayi alt alt";
                    column-gap: 8px; row-gap: 2px; align-items: center; align-content: start;
                    transition: border-color .15s, box-shadow .15s, background-color .15s, transform .15s; }
        .sy-sekme::after { content: ''; position: absolute; left: 14px; right: 14px; bottom: 0; height: 3px; border-radius: 3px 3px 0 0;
                           background: var(--t); opacity: 0; transition: opacity .15s; }
        .sy-sekme:hover { border-color: var(--t-kenar); box-shadow: var(--sy-golge); transform: translateY(-1px); }
        .sy-sekme:focus-visible { outline: 2px solid var(--t); outline-offset: 2px; }
        .sy-sekme.secili { background: var(--t-zemin); border-color: var(--t-kenar); }
        .sy-sekme.secili::after { opacity: 1; }
        .sy-sekme-ikon { grid-area: ikon; width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center;
                         font-size: 16px; color: var(--t); background: var(--t-zemin); transition: background-color .15s; }
        .sy-sekme.secili .sy-sekme-ikon { background: var(--t-zemin2); }
        .sy-sekme.bos:not(.secili) .sy-sekme-ikon { color: var(--hb-soluk); background: var(--hb-dolgu); }
        .sy-sekme-ad { grid-area: ad; font-size: 13px; font-weight: 600; color: var(--hb-ikincil); }
        .sy-sekme.secili .sy-sekme-ad { color: var(--hb-yazi); }
        .sy-sekme-sayi { grid-area: sayi; align-self: baseline; font-size: 28px; font-weight: 700; line-height: 1.15;
                         letter-spacing: -.01em; font-variant-numeric: tabular-nums; }
        .sy-sekme.secili .sy-sekme-sayi, .sy-sekme.acil .sy-sekme-sayi { color: var(--t-sayi); }
        .sy-sekme.bos .sy-sekme-sayi { color: var(--hb-soluk); }
        .sy-sekme-alt { grid-area: alt; align-self: baseline; font-size: 12px; line-height: 1.3; color: var(--hb-ikincil); }
        @media (prefers-reduced-motion: reduce) { .sy-sekme, .sy-sekme::after { transition: none; } .sy-sekme:hover { transform: none; } }
        .sy-ust-sag { display: flex; flex-direction: column; align-items: flex-end; justify-content: space-between; gap: 6px; }
        .sy-suzgec { display: flex; align-items: center; gap: 8px 10px; flex-wrap: wrap; margin-bottom: 12px; }
        .sy-suzgec .ant-picker { width: 260px; }
        .sy-suzgec .ant-select { min-width: 220px; max-width: 420px; }
        .sy-bolum { font-size: 12px; color: var(--hb-ikincil); text-transform: uppercase; letter-spacing: .04em; margin: 16px 0 8px; }
        .sy-bolum:first-child { margin-top: 0; }
        .sy-liste { display: flex; flex-direction: column; gap: 8px; }
        .sy-satir { --sy-ton: var(--sy-k-bekle); display: grid; grid-template-columns: 124px minmax(0, 1fr) 170px auto;
                    grid-template-areas: "tarih orta tutar eylem"; column-gap: 16px; align-items: center; cursor: pointer;
                    padding: 12px 16px 12px 14px; border: 1px solid var(--hb-kenar); border-left: 4px solid var(--sy-ton);
                    border-radius: 10px; background: var(--hb-yuzey); outline: none; transition: box-shadow .15s, border-color .15s; }
        .sy-satir:hover, .sy-satir:focus-visible { border-color: var(--hb-birincil); border-left-color: var(--sy-ton); box-shadow: var(--sy-golge); }
        .sy-hata { --sy-ton: var(--sy-k-hata); }
        .sy-eylem { --sy-ton: var(--sy-k-eylem); }
        .sy-tamam { --sy-ton: var(--sy-k-tamam); }
        .sy-tarih { grid-area: tarih; padding-right: 12px; border-right: 1px solid var(--hb-kenar); }
        .sy-tarih-ust { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .sy-tarih-alt { font-size: 12px; color: var(--hb-ikincil); white-space: nowrap; }
        .sy-orta { grid-area: orta; min-width: 0; }
        .sy-baslik { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .sy-il { font-size: 16px; font-weight: 600; }
        .sy-adim { display: flex; align-items: baseline; gap: 6px; margin-top: 4px; font-size: 13px; color: var(--hb-ikincil); }
        .sy-adim .anticon { color: var(--sy-ton); }
        .sy-hata .sy-adim { color: var(--hb-hata); font-weight: 500; }
        .sy-eylem .sy-adim { color: var(--hb-yazi); }
        .sy-tutar { grid-area: tutar; text-align: right; }
        .sy-tutar-deger { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; }
        .sy-tutar-alt { font-size: 12px; color: var(--hb-ikincil); }
        .sy-satir .sy-eylem { grid-area: eylem; display: flex; align-items: center; justify-content: flex-end; gap: 12px; }
        .sy-ok { color: var(--hb-ikincil); font-size: 12px; }
        /* DarkMode.css koyu temada etiket / değerlere zemin veriyor (body[data-theme='dark'] seçicisinden güçlü olmalı) */
        body .sy-c-bilgi.ant-descriptions .ant-descriptions-item-label,
        body .sy-c-bilgi.ant-descriptions .ant-descriptions-item-content { background: transparent !important; }
        .sy-c-baslik { font-size: 18px; font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .sy-c-alt-bar { display: flex; justify-content: space-between; align-items: center; gap: 8px 16px; flex-wrap: wrap; }
        .sy-c-ozet { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 14px; font-size: 13px; color: var(--hb-ikincil); }
        .sy-c-ozet b { color: var(--hb-yazi); font-size: 16px; font-variant-numeric: tabular-nums; }
        @media (max-width: 640px) {
          .hb-baslik { display: none; }
          .hb-satir { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
          .hb-h-gun { grid-row: 1; grid-column: 1; flex-direction: row; flex-wrap: wrap; gap: 0 6px; align-items: baseline; justify-content: flex-start; }
          .hb-h-toplam { grid-row: 1; grid-column: 2; }
          .hb-h-ogle { grid-row: 2; grid-column: 1; }
          .hb-h-aksam { grid-row: 2; grid-column: 2; }
          .hb-h-ayrinti { grid-row: 3; grid-column: 1 / -1; }
          .hb-karo-resim { display: none; }
          .hb-karo { padding: 8px; }
          .hb-karo-tutar { font-size: 15px; white-space: nowrap; }
          .hb-h-konum-etiket { margin-top: 0; }
          .hb-h-konum { grid-row: auto; flex-wrap: wrap; }
          .hb-h-konum .ant-btn { flex: 1 1 100%; }
          .hb-konum-kart .ant-btn { flex: 1 1 100%; }
          .sy-sekmeler { flex-basis: 100%; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
          .sy-sekme { grid-template-columns: auto minmax(0, 1fr); grid-template-areas: "ikon ad" "ikon sayi"; column-gap: 10px;
                      row-gap: 0; padding: 8px 10px 10px 8px; }
          .sy-sekme::after { left: 10px; right: 10px; }
          .sy-sekme:hover { transform: none; }
          .sy-sekme-ikon { width: 36px; height: 36px; font-size: 17px; }
          .sy-sekme-ad { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .sy-sekme-sayi { align-self: start; font-size: 20px; line-height: 1.1; }
          .sy-sekme-alt { display: none; }
          .sy-ust-sag { flex-basis: 100%; flex-direction: row; align-items: center; flex-wrap: wrap; }
          .sy-suzgec .ant-picker, .sy-suzgec .ant-select { width: 100%; max-width: none; flex: 1 1 100%; }
          /* Tarih aralığı açılırı iki takvimi yan yana açıp ekrandan taşıyordu: hazır aralıklar üstte, takvimler alt alta */
          .ant-picker-dropdown .ant-picker-panel-layout, .ant-picker-dropdown .ant-picker-panels { flex-direction: column; }
          .ant-picker-dropdown .ant-picker-presets { width: 100% !important; max-width: none !important; border-inline-end: 0 !important;
                                                     border-bottom: 1px solid rgba(128, 128, 128, 0.25); }
          .ant-picker-dropdown .ant-picker-presets ul { flex: none !important; height: auto !important; overflow: visible !important;
                                                        display: flex; flex-wrap: wrap; gap: 4px; padding: 6px 8px; }
          .ant-picker-dropdown .ant-picker-presets ul li { margin: 0 !important; }
          .sy-satir { grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "tarih tutar" "orta orta" "eylem eylem";
                      row-gap: 6px; padding: 10px 12px; }
          .sy-eylemsiz { grid-template-areas: "tarih tutar" "orta orta"; }
          .sy-eylemsiz .sy-eylem { display: none; }
          .sy-tarih { border-right: 0; padding-right: 0; display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; }
          .sy-satir .sy-eylem .ant-btn { flex: 1; }
          .sy-ok { display: none; }
        }
      `}</style>
      {riza && !riza.aydinlatma_okundu && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Fiş yüklemek ve konum doğrulamak için önce aydınlatma metnini okuyun."
          action={<Button size="small" onClick={() => setAydinlatmaAcik(true)}>Oku</Button>} />
      )}
      <div className="sy-ust">
        <div className="sy-sekmeler" role="tablist" aria-label="Seyahat grupları">
          {SEKMELER.map((t) => (
            <button key={t.k} type="button" role="tab" aria-selected={etkin === t.k} onClick={() => setSuzgec(t.k)} style={sekmeRengi(t.ton)}
              className={`sy-sekme${etkin === t.k ? ' secili' : ''}${t.k === 'yapilacak' && sayi.yapilacak ? ' acil' : ''}${sayi[t.k] ? '' : ' bos'}`}>
              <span className="sy-sekme-ikon" aria-hidden>{t.ikon}</span>
              <span className="sy-sekme-ad">{t.ad}</span>
              <span className="sy-sekme-sayi">{sayi[t.k]}</span>
              <span className="sy-sekme-alt">{t.alt}</span>
            </button>
          ))}
        </div>
        <div className="sy-ust-sag">
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => setPlan({ acik: true, seyahat: null })}>Seyahat Planla</Button>
          <Space size={0} wrap>
            {riza?.aydinlatma_okundu && (
              <Tooltip title={okumaAcik ? `Açık rıza ${tarih(riza.riza_zamani)}: fişleriniz okuma için yurt dışına (Anthropic) gönderilir.`
                : 'Açık rıza yok: fişleriniz yurt dışına gönderilmez, tutarı elle girersiniz.'}>
                <Button type="text" size="small" icon={<SafetyCertificateOutlined />} onClick={() => setAydinlatmaAcik(true)}>
                  Fiş Okuma: <b style={{ marginLeft: 4 }}>{okumaAcik ? 'Açık' : 'Kapalı'}</b>
                </Button>
              </Tooltip>
            )}
            <Popover title="Nasıl Çalışır?" content={nasil} trigger="click" placement="bottomRight">
              <Button type="text" size="small" icon={<QuestionCircleOutlined />}>Nasıl Çalışır?</Button>
            </Popover>
          </Space>
        </div>
      </div>
      {liste.length > 0 && (
        <div className="sy-suzgec">
          <DatePicker.RangePicker value={tarihSuz} onChange={setTarihSuz} presets={ARALIKLAR} format="DD.MM.YYYY" allowClear
            placeholder={['Başlangıç', 'Bitiş']} aria-label="Tarih aralığı" />
          <Select mode="multiple" allowClear placeholder="Tüm Şehirler" value={ilSuz} onChange={setIlSuz} maxTagCount="responsive"
            optionFilterProp="label" aria-label="Şehir" options={ilSecenekleri}
            optionRender={(o) => (
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>{o.data.label}</span><Text type="secondary" style={{ fontSize: 12 }}>{o.data.ek} Seyahat</Text>
              </span>
            )} />
          {filtreli && (
            <>
              <Text type="secondary" style={{ fontSize: 13 }}>{suzulmus.length} / {liste.length} Seyahat</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={temizle}>Filtreleri Temizle</Button>
            </>
          )}
        </div>
      )}
      {filtreli && !suzulmus.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bu tarih ve şehirde seyahat yok.">
          <Button onClick={temizle}>Filtreleri Temizle</Button>
        </Empty>
      )}
      {(!filtreli || suzulmus.length > 0) && bolumler.map((k) => {
        const l = grubu(k);
        if (!l.length && etkin === 'tumu') return null;
        return (
          <section key={k}>
            {etkin === 'tumu' && <div className="sy-bolum">{SEKMELER.find((t) => t.k === k).ad} · {l.length}</div>}
            {l.length ? (
              <div className="sy-liste">
                {l.map(({ s, adim }) => (
                  <SeyahatSatiri key={s.id} s={s} adim={adim} onAc={(x) => setSeciliId(x.id)} onEylem={eylem} bekle={konumBekle === s.id} />
                ))}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={filtreli ? 'Bu filtrelerle bu grupta seyahat yok.' : {
                yapilacak: 'Şu an sizden bekleyen bir iş yok.', surecte: 'Onayda ya da yaklaşan seyahat yok.',
                tamam: 'Henüz onaylanan harcırah yok.',
              }[k]} />
            )}
          </section>
        );
      })}
      {!liste.length && <Empty description="Henüz seyahatiniz yok. “Seyahat Planla” ile başlayın." />}
      <SeyahatCekmecesi s={secili} degiskenler={degiskenler} onKapat={() => setSeciliId(null)} onGir={tutarGir} onGonder={gonder}
        onGeriCek={geriCek} onGiderEkle={giderEkle} onGiderDuzenle={giderDuzenle} onHak={hakDegistir} onPlan={planIslem}
        onKonum={konumDogrula} konumBekle={konumBekle} onKonumAciklama={(sy, t, o) => setKonumAciklama({ s: sy, tarih: t, konum: o })} />
      <KonumAciklamaPenceresi istek={konumAciklama} onKapat={() => setKonumAciklama(null)} onKaydet={konumAciklamaKaydet} />
      <TutarPenceresi istek={istek} okumaAcik={okumaAcik} onKapat={() => setIstek(null)}
        onKaydedildi={(s) => { guncelle(s); setIstek(null); }} />
      <GiderPenceresi istek={giderIstek} okumaAcik={okumaAcik} onKapat={() => setGiderIstek(null)}
        onKaydedildi={(s) => { guncelle(s); setGiderIstek(null); }} />
      <PlanPenceresi acik={plan.acik} seyahat={plan.seyahat} onKapat={() => setPlan({ acik: false, seyahat: null })}
        onKaydedildi={(s) => { guncelle(s); setPlan({ acik: false, seyahat: null }); setSuzgec('yapilacak'); setSeciliId(s.id); }} />
      <AydinlatmaPenceresi acik={aydinlatmaAcik} riza={riza} onKapat={() => setAydinlatmaAcik(false)} onKaydet={rizaKaydet} />
    </div>
  );
}
